import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Mail } from 'lucide-react';

import { Img } from '../components/Img';
import Seo from '../components/Seo';
import { useLang } from '../i18n';
import { FALLBACK_IMAGE as FALLBACK } from '../lib/api';
import { useCategories } from '../lib/useCatalog';

// Foto d'ambiente curate, una per mondo: raccontano dove vive l'oggetto,
// cosa che una foto su fondo bianco non fa. Un mondo senza scatto d'ambiente
// ripiega sull'immagine di prodotto che arriva dal feed. Fonte: campagna
// Radici (COMMERCIALE_VDEH/NAUSICAA), tutti scatti reali.
const SCATTI = {
  'per-la-tavola': '/assets/mondi/per-la-tavola.webp',
  'per-la-cottura': '/assets/mondi/per-la-cottura.webp',
  'per-gli-ambienti': '/assets/mondi/per-gli-ambienti.webp',
  'per-il-bagno': '/assets/mondi/per-il-bagno.webp',
  'per-le-feste': '/assets/mondi/per-le-feste.webp',
  'per-il-tempo-libero': '/assets/mondi/per-il-tempo-libero.webp',
  'per-laria-aperta': '/assets/mondi/per-laria-aperta.webp',
  'per-la-dispensa': '/assets/mondi/per-la-dispensa.webp',
};

const SALES_EMAIL = 'commerciale@vde.it';

/**
 * Hub catalogo: un indice, non una vetrina. Feedback 02/11/2026 — chi apre
 * il catalogo e' spesso un addetto ai lavori, quindi niente storytelling
 * (quello sta in /collezioni, che ha la sua gabbia a card grandi): mondi in
 * righe, tutte le categorie a vista, contatto dell'ufficio commerciale in
 * testa. Nessun conteggio in pagina — il sito non dichiara le giacenze.
 */
export default function Catalog() {
  const { lang, t } = useLang();
  const { data, isLoading, isError } = useCategories(lang);
  const macros = data?.macros ?? [];

  return (
    <>
      <Seo
        title={t('catalog.seoTitle')}
        description={t('catalog.seoDesc')}
        path="/catalogo"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: '/' },
            { '@type': 'ListItem', position: 2, name: t('catalog.title'), item: '/catalogo' },
          ],
        }}
      />

      <header className="py-12 md:py-16">
        <div className="wrap grid gap-10 lg:grid-cols-[1fr_380px] lg:items-end lg:gap-16">
          <div>
            <h1 className="text-[clamp(2.25rem,4.6vw,3.75rem)] leading-[1.02]">{t('catalog.title')}</h1>
            <p className="prosa mt-6 border-t-2 border-inchiostro pt-5 text-lg">{t('catalog.intro')}</p>
          </div>

          <aside data-testid="catalog-sales" className="bg-inchiostro p-6 text-carta md:p-7">
            <p className="text-[14px] font-bold text-carta/70">{t('catalog.salesKicker')}</p>
            <p className="mt-2 font-display text-2xl leading-tight">{t('catalog.salesTitle')}</p>
            <a
              href={`mailto:${SALES_EMAIL}`}
              data-testid="catalog-sales-email"
              className="mt-5 inline-flex items-center gap-2 text-base font-semibold text-ocra link-ul"
            >
              <Mail size={15} strokeWidth={1.8} />
              {SALES_EMAIL}
            </a>
            <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 border-t border-carta/20 pt-4 text-[14px] font-semibold">
              <Link to="/professionisti/rivenditori" className="link-ul text-carta/85 hover:text-ocra">
                {t('nav.reseller')}
              </Link>
              <Link to="/professionisti/horeca" className="link-ul text-carta/85 hover:text-ocra">
                {t('nav.horeca')}
              </Link>
            </div>
          </aside>
        </div>
      </header>

      {isError && (
        <div className="wrap py-20">
          <p className="prosa">{t('catalog.loadError')}</p>
        </div>
      )}

      {isLoading && (
        <div className="wrap grid gap-4 py-10" aria-busy="true">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-28 bg-carta-2" />
          ))}
        </div>
      )}

      {!isLoading && !isError && (
        <ul className="wrap pb-14 md:pb-20">
          {macros.map((macro) => (
            <li
              key={macro.slug}
              data-testid={`world-row-${macro.slug}`}
              className="grid grid-cols-[88px_1fr] gap-x-5 gap-y-3 border-t border-inchiostro/15 py-6 md:grid-cols-[160px_1fr_auto] md:gap-x-8 md:py-7"
            >
              <Link to={`/catalogo/mondo/${macro.slug}`} tabIndex={-1} aria-hidden className="block">
                <div className="aspect-[4/3] overflow-hidden bg-carta-2">
                  <Img
                    src={SCATTI[macro.slug] || macro.image || FALLBACK}
                    alt=""
                    onError={(e) => {
                      e.currentTarget.src = FALLBACK;
                    }}
                    className={
                      SCATTI[macro.slug]
                        ? 'h-full w-full object-cover'
                        : 'h-full w-full object-contain p-3 mix-blend-multiply'
                    }
                  />
                </div>
              </Link>
              <div>
                <h2 className="font-display text-2xl md:text-[28px]">
                  <Link
                    to={`/catalogo/mondo/${macro.slug}`}
                    data-testid={`world-card-${macro.slug}`}
                    className="transition-colors hover:text-marsala"
                  >
                    {macro.label}
                  </Link>
                </h2>
                <ul className="mt-3 flex flex-wrap gap-2">
                  {macro.categories.map((c) => (
                    <li key={c.slug}>
                      <Link
                        to={`/catalogo/${c.slug}`}
                        data-testid={`catalog-category-${c.slug}`}
                        className="inline-flex min-h-[36px] items-center border border-inchiostro/20 px-3 text-[14px] font-semibold text-inchiostro/80 transition-colors hover:border-marsala hover:text-marsala"
                      >
                        {c.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
              <Link
                to={`/catalogo/mondo/${macro.slug}`}
                className="col-start-2 inline-flex items-center gap-2 self-start text-[14px] font-bold text-marsala link-ul md:col-start-3 md:mt-2"
              >
                {t('catalog.worldAll')}
                <ArrowRight size={14} strokeWidth={2} />
              </Link>
            </li>
          ))}
        </ul>
      )}

      {/* le collezioni hanno la loro pagina e il loro tono: qui solo il rimando */}
      <p className="wrap border-t border-inchiostro/15 py-10 text-base text-inchiostro/70">
        {t('catalog.collectionsHint')}{' '}
        <Link to="/collezioni" data-testid="catalog-to-collections" className="font-semibold text-marsala link-ul">
          {t('catalog.collectionsHintCta')}
        </Link>
      </p>
    </>
  );
}
